import CropperModal from '@/components/Cropper';
import { hidePopup, showPopup } from '@/components/PopupContainer/popupService';
import { WebCamera } from '@/components/WebCamera';
import { handleOpenPhotoViewer } from '@/features/users/utils';
import { useCallback, useRef, useState } from 'react';

const MODAL_IDS = {
   CAMERA: 'avatar_camera_popup',
   CROPPER: 'avatar_cropper_popup',
};

/**
 * ViewModel — AvatarPhotoPicker
 *
 * Manages all state and business logic for picking, capturing,
 * cropping and deleting an avatar image.
 * WebCamera and CropperModal are surfaced via showPopup.
 *
 * @param {string|null} initialImage - Pre-filled image URL (e.g. from profile/group data)
 * @param {Function}    onChange     - Called with (blob|null) whenever the image changes
 */
export const useAvatarPhotoPicker = ({ initialImage = null, onChange }) => {
   const prevInitialImageRef = useRef(initialImage);
   const [imageSrc, setImageSrc] = useState(initialImage || null);
   const fileInputRef = useRef(null);

   // Sync initialImage whenever the parent provides a new value (ref-guarded to avoid effect lint)
   if (prevInitialImageRef.current !== initialImage) {
      prevInitialImageRef.current = initialImage;
      setImageSrc(initialImage || null);
   }

   // ── Internal helpers ──────────────────────────────────────────────────────

   const closeCamera = useCallback(() => {
      hidePopup('avatar_camera_popup');
   }, []);

   const closeCropper = useCallback(() => {
      hidePopup('avatar_cropper_popup');
   }, []);

   // ── Crop confirm ──────────────────────────────────────────────────────────

   const handleCropConfirm = useCallback(
      blob => {
         const previewUrl = URL.createObjectURL(blob);
         setImageSrc(previewUrl);
         closeCropper();
         onChange?.(blob);
      },
      [closeCropper, onChange],
   );

   // ── Open cropper (shared by upload and camera flows) ─────────────────────

   const openCropper = useCallback(
      src => {
         showPopup({
            id: MODAL_IDS.CROPPER,
            title: 'Drag and Adjust',
            body: <CropperModal src={src} onCrop={handleCropConfirm} circularCrop />,
            className: 'avatar-cropper-popup-modal',
            visible: true,
            disableOverlayClick: true,
         });
      },
      [handleCropConfirm],
   );

   // ── File Upload ───────────────────────────────────────────────────────────

   const handleUploadClick = useCallback(() => {
      fileInputRef.current?.click();
   }, []);

   const handleFileChange = useCallback(
      e => {
         const file = e.target.files?.[0];
         if (!file) return;
         e.target.value = ''; // reset so same file can be picked again
         const objectUrl = URL.createObjectURL(file);
         openCropper(objectUrl);
      },
      [openCropper],
   );

   // ── Camera ────────────────────────────────────────────────────────────────

   const handleCameraCapture = useCallback(
      ({ dataUri }) => {
         closeCamera();
         openCropper(dataUri);
      },
      [closeCamera, openCropper],
   );

   const handleOpenCamera = useCallback(() => {
      showPopup({
         id: MODAL_IDS.CAMERA,
         title: 'Take Photo',
         body: <WebCamera onCapture={handleCameraCapture} onClose={closeCamera} />,
         className: 'avatar-camera-popup-modal',
         visible: true,
      });
   }, [handleCameraCapture, closeCamera]);

   // ── Delete ────────────────────────────────────────────────────────────────

   const handleDelete = useCallback(() => {
      setImageSrc(null);
      onChange?.(null);
   }, [onChange]);

   const handleViewPhoto = useCallback(() => {
      if (!imageSrc) return;
      handleOpenPhotoViewer({ imageUri: imageSrc });
   }, [imageSrc]);

   return {
      state: { imageSrc },
      refs: { fileInputRef },
      handlers: {
         handleUploadClick,
         handleFileChange,
         handleOpenCamera,
         handleDelete,
         handleViewPhoto,
      },
   };
};
